import { createSlice } from "@reduxjs/toolkit";
import { emptyResource, failLoading, finishLoading, Resource, startLoading } from "shared/state/resource";
import { GetTenantForListResponse, TenantDTO } from "./tenantDtos";
import { DeactivateTenant, GetActiveTenants, GetInactiveTenants, GetTenantOverview, ReactivateTenant } from "./tenantThunks";

/**
 * What the tenant screens keep between requests, each server record once. Everything else lives
 * where it is used:
 * - a row or a section being edited is passed to its modal, not copied here;
 * - form lookups (countries, employees, banks) are fetched by the modal that needs them;
 * - form values live in the form, UI state in the component or the URL;
 * - messages are toasts dispatched by the thunks.
 *
 * Not persisted (see the blacklist in app/store.ts): the pages load their data from the URL.
 */
export interface TenantState {
    activeTenants: Resource<GetTenantForListResponse[]>;
    inactiveTenants: Resource<GetTenantForListResponse[]>;
    /** The tenant page's tenant, with its locations, their responsibilities, work time and bank data. */
    overview: Resource<TenantDTO>;
}

export const initialTenantState: TenantState = {
    activeTenants: emptyResource(),
    inactiveTenants: emptyResource(),
    overview: emptyResource(),
};

const tenantSlice = createSlice({
    name: "tenantSlice",
    initialState: initialTenantState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(GetActiveTenants.pending, (state, action) => startLoading(state.activeTenants, action.meta.requestId))
            .addCase(GetActiveTenants.fulfilled, (state, action) => finishLoading(state.activeTenants, action.meta.requestId, action.payload))
            .addCase(GetActiveTenants.rejected, (state, action) => failLoading(state.activeTenants, action.meta.requestId))

            .addCase(GetInactiveTenants.pending, (state, action) => startLoading(state.inactiveTenants, action.meta.requestId))
            .addCase(GetInactiveTenants.fulfilled, (state, action) => finishLoading(state.inactiveTenants, action.meta.requestId, action.payload))
            .addCase(GetInactiveTenants.rejected, (state, action) => failLoading(state.inactiveTenants, action.meta.requestId))

            .addCase(GetTenantOverview.pending, (state, action) => startLoading(state.overview, action.meta.requestId))
            .addCase(GetTenantOverview.fulfilled, (state, action) => finishLoading(state.overview, action.meta.requestId, action.payload))
            .addCase(GetTenantOverview.rejected, (state, action) => failLoading(state.overview, action.meta.requestId))

            // The row leaves the list on screen; the other list is loaded again when it is opened.
            .addCase(DeactivateTenant.fulfilled, (state, action) => {
                state.activeTenants.data = state.activeTenants.data?.filter(tenant => tenant.id !== action.meta.arg) ?? null;
                if (state.overview.data?.id === action.meta.arg) state.overview.data.isActive = false;
            })
            .addCase(ReactivateTenant.fulfilled, (state, action) => {
                state.inactiveTenants.data = state.inactiveTenants.data?.filter(tenant => tenant.id !== action.meta.arg) ?? null;
                if (state.overview.data?.id === action.meta.arg) state.overview.data.isActive = true;
            });
    },
});

export default tenantSlice.reducer;
